import React from 'react';

import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined';
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined';
import AgricultureOutlinedIcon from '@mui/icons-material/AgricultureOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';

type ServicesMaintenanceRefrigerationSafetyProps = {
  onBack: () => void;
};

export default function ServicesMaintenanceRefrigerationSafety({
  onBack,
}: ServicesMaintenanceRefrigerationSafetyProps): React.ReactElement {
  return (
    <section className="bg-white">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-20">
        <div className="mx-auto max-w-5xl">
          <button
            onClick={onBack}
            className="mb-6 flex items-center gap-2 text-sky-600 hover:text-sky-700 transition-colors"
          >
            <ArrowBackOutlinedIcon fontSize="small" />
            <span className="text-sm font-semibold">Back to Services</span>
          </button>

          {/* Maintenance Section */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-sky-600 text-white text-sm font-semibold mb-4">
                Service Six
              </span>
              <h2 className="text-3xl font-semibold leading-tight text-slate-900 sm:text-4xl">
                Maintenance, Refrigeration & Safety Equipment
              </h2>
              <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg max-w-3xl mx-auto">
                In complex operating environments, system reliability is critical. Ancient Engineering provides
                structured maintenance solutions that extend asset lifespan, reduce downtime, and ensure operational
                continuity.
              </p>
            </div>

            {/* Maintenance Section */}
            <div className="mb-12">
              <h3 className="text-2xl font-semibold text-slate-900 mb-6 text-center">Maintenance</h3>
              <div className="mb-8">
                <h4 className="text-xl font-semibold text-slate-900 mb-4 text-center">
                  Sector-Specific Maintenance Engineering
                </h4>
                <div className="grid gap-6 lg:grid-cols-2">
                  {/* Residential & Commercial */}
                  <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                        <HomeOutlinedIcon fontSize="medium" />
                      </div>
                      <h5 className="text-lg font-semibold text-slate-900">Residential & Commercial</h5>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-600 mb-3">
                      Planned and reactive maintenance for buildings and facilities to maintain safety, functionality,
                      and compliance.
                    </p>
                    <ul className="space-y-2 text-sm leading-relaxed text-slate-600">
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        <span>Electrical and mechanical maintenance</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        <span>Preventive inspections and fault rectification</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        <span>Lighting, power, and infrastructure upkeep</span>
                      </li>
                    </ul>
                  </div>

                  {/* Industrial & Production */}
                  <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                        <FactoryOutlinedIcon fontSize="medium" />
                      </div>
                      <h5 className="text-lg font-semibold text-slate-900">Industrial & Production</h5>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-600 mb-3">
                      Maintenance programs designed for high-load, continuous-use environments.
                    </p>
                    <ul className="space-y-2 text-sm leading-relaxed text-slate-600">
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        <span>Preventive and corrective maintenance</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        <span>Equipment diagnostics and performance optimisation</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        <span>Shutdown and restart support</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* End-to-End Maintenance Execution */}
              <div className="mb-8">
                <h4 className="text-xl font-semibold text-slate-900 mb-4 text-center">
                  End-to-End Maintenance Execution
                </h4>
                <div className="grid gap-6 lg:grid-cols-2">
                  <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                        <EngineeringOutlinedIcon fontSize="medium" />
                      </div>
                      <h5 className="text-lg font-semibold text-slate-900">Planned Preventive Maintenance (PPM)</h5>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-600">
                      Engineered maintenance schedules that identify and address failures before they impact operations.
                    </p>
                  </div>

                  <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                        <BuildOutlinedIcon fontSize="medium" />
                      </div>
                      <h5 className="text-lg font-semibold text-slate-900">Corrective & Emergency Maintenance</h5>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-600">
                      Rapid-response maintenance to restore functionality and minimise operational losses.
                    </p>
                  </div>
                </div>
              </div>

              {/* Why Choose Our Maintenance */}
              <div className="rounded-2xl bg-sky-600 p-8 lg:p-10 text-center mb-8">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <BuildOutlinedIcon className="text-white" fontSize="large" />
                  <h4 className="text-2xl font-semibold text-white">Why Choose Our Maintenance?</h4>
                </div>
                <p className="text-base leading-relaxed text-white/90 max-w-2xl mx-auto">
                  We don't just maintain systems — we engineer reliability through proactive planning, technical
                  expertise, and disciplined execution.
                </p>
              </div>
            </div>

            {/* Refrigeration Section */}
            <div className="mb-12">
              <h3 className="text-2xl font-semibold text-slate-900 mb-6 text-center">Refrigeration</h3>
              <p className="text-base leading-relaxed text-slate-600 mb-8 text-center max-w-3xl mx-auto">
                In temperature-controlled environments, precision and reliability are non-negotiable. Ancient Engineering
                delivers engineered refrigeration systems that safeguard product quality and energy efficiency.
              </p>

              <div className="mb-8">
                <h4 className="text-xl font-semibold text-slate-900 mb-4 text-center">
                  Sector-Specific Refrigeration Solutions
                </h4>
                <div className="grid gap-6 lg:grid-cols-2">
                  {/* Commercial & Industrial */}
                  <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                        <FactoryOutlinedIcon fontSize="medium" />
                      </div>
                      <h5 className="text-lg font-semibold text-slate-900">Commercial & Industrial</h5>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-600 mb-3">
                      High-performance refrigeration systems for retail, processing, and industrial applications.
                    </p>
                    <ul className="space-y-2 text-sm leading-relaxed text-slate-600">
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        <span>Cold rooms and freezer rooms</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        <span>Chillers and walk-in refrigeration</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        <span>Temperature monitoring and control systems</span>
                      </li>
                    </ul>
                  </div>

                  {/* Agriculture & Food Processing */}
                  <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                        <AgricultureOutlinedIcon fontSize="medium" />
                      </div>
                      <h5 className="text-lg font-semibold text-slate-900">Agriculture & Food Processing</h5>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-600 mb-3">
                      Cold-chain solutions that protect produce quality from harvest through distribution.
                    </p>
                    <ul className="space-y-2 text-sm leading-relaxed text-slate-600">
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        <span>Agricultural cold storage facilities</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        <span>Packhouse and processing refrigeration</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        <span>Dairy and meat cooling systems</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* End-to-End Refrigeration Engineering */}
              <div className="mb-8">
                <h4 className="text-xl font-semibold text-slate-900 mb-4 text-center">
                  End-to-End Refrigeration Engineering
                </h4>
                <div className="grid gap-6 lg:grid-cols-2">
                  <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                        <EngineeringOutlinedIcon fontSize="medium" />
                      </div>
                      <h5 className="text-lg font-semibold text-slate-900">System Design & Load Calculations</h5>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-600">
                      Accurate cooling assessments to ensure efficiency, reliability, and reduced operating costs.
                    </p>
                  </div>

                  <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                        <VerifiedOutlinedIcon fontSize="medium" />
                      </div>
                      <h5 className="text-lg font-semibold text-slate-900">
                        Installation, Commissioning & Maintenance
                      </h5>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-600">
                      Professional installation, testing, and ongoing servicing to meet safety and performance
                      standards.
                    </p>
                  </div>
                </div>
              </div>

              {/* Why Choose Our Refrigeration */}
              <div className="rounded-2xl bg-sky-600 p-8 lg:p-10 text-center mb-8">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <AcUnitOutlinedIcon className="text-white" fontSize="large" />
                  <h4 className="text-2xl font-semibold text-white">Why Choose Our Refrigeration?</h4>
                </div>
                <p className="text-base leading-relaxed text-white/90 max-w-2xl mx-auto">
                  We don't just install cooling equipment — we engineer cold-chain systems built for longevity and
                  compliance.
                </p>
              </div>
            </div>

            {/* Safety Equipment & Hardware Section */}
            <div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-6 text-center">
                Safety Equipment & Hardware
              </h3>
              <p className="text-base leading-relaxed text-slate-600 mb-8 text-center max-w-3xl mx-auto">
                Safety is a system, not an afterthought. Ancient Engineering supplies certified safety equipment and
                industrial hardware designed to protect people, assets, and operations.
              </p>

              <div className="mb-8">
                <h4 className="text-xl font-semibold text-slate-900 mb-4 text-center">
                  Safety & Hardware Solutions
                </h4>
                <div className="grid gap-6 lg:grid-cols-2">
                  {/* Personal Protective Equipment */}
                  <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                        <SecurityOutlinedIcon fontSize="medium" />
                      </div>
                      <h5 className="text-lg font-semibold text-slate-900">
                        Personal Protective Equipment (PPE)
                      </h5>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-600 mb-3">
                      Certified PPE for industrial, commercial, and construction environments.
                    </p>
                    <ul className="space-y-2 text-sm leading-relaxed text-slate-600">
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        <span>Helmets, gloves, safety boots, and eyewear</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        <span>High-visibility and flame-resistant clothing</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        <span>Respiratory and fall-protection equipment</span>
                      </li>
                    </ul>
                  </div>

                  {/* Industrial Hardware & Safety Systems */}
                  <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                        <BuildOutlinedIcon fontSize="medium" />
                      </div>
                      <h5 className="text-lg font-semibold text-slate-900">
                        Industrial Hardware & Safety Systems
                      </h5>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-600 mb-3">
                      Durable, standards-compliant hardware for demanding operational environments.
                    </p>
                    <ul className="space-y-2 text-sm leading-relaxed text-slate-600">
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        <span>Fire safety equipment and signage</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        <span>Lockout/tagout systems</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        <span>Tools, fasteners, and industrial hardware</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Quality & Compliance Assurance */}
              <div className="mb-8">
                <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200 max-w-2xl mx-auto">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                      <CheckCircleOutlinedIcon fontSize="medium" />
                    </div>
                    <h5 className="text-lg font-semibold text-slate-900">Quality & Compliance Assurance</h5>
                  </div>
                  <p className="text-sm leading-relaxed text-slate-600">
                    All safety equipment and hardware supplied meets relevant safety standards and regulatory
                    requirements.
                  </p>
                </div>
              </div>

              {/* Why Choose Our Safety Equipment & Hardware */}
              <div className="rounded-2xl bg-sky-600 p-8 lg:p-10 text-center">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <SecurityOutlinedIcon className="text-white" fontSize="large" />
                  <h4 className="text-2xl font-semibold text-white">Why Choose Our Safety Equipment & Hardware?</h4>
                </div>
                <p className="text-base leading-relaxed text-white/90 max-w-2xl mx-auto">
                  We don't just supply products — we supply protection backed by compliance, quality, and technical
                  support.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
